import { useCallback, useEffect, useRef, useState } from 'react';
import { stationCompanions } from '../datas/stationCompanions';
import { getAllCompanions, getFeaturedCompanions } from '../services/api';
import { Companion } from '../types';

interface UseAllCompanionsInfiniteScrollOptions {
  rootMargin?: string;
  scrollContainerRef?: React.RefObject<HTMLElement | null>;
  enabled?: boolean;
}

interface UseAllCompanionsInfiniteScrollReturn {
  loading: boolean;
  hasMore: boolean;
  error: string | null;
  loadMore: () => Promise<void>;
  reset: () => void;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
  companions: Companion[];
}

// Fisher-Yates shuffle algorithm
const shuffleArray = <T>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

/**
 * Custom hook for managing infinite scroll of ALL companions
 * Loads featured companions first, then randomly sorted regular companions
 */
export const useAllCompanionsInfiniteScroll = (
  options: UseAllCompanionsInfiniteScrollOptions = {},
): UseAllCompanionsInfiniteScrollReturn => {
  const { rootMargin = '200px', scrollContainerRef, enabled = true } = options;

  // State
  const [companions, setCompanions] = useState<Companion[]>([]);
  const [allCompanionsList, setAllCompanionsList] = useState<Companion[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Refs
  const offsetRef = useRef(0);
  const loadingRef = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const initialLoadTriggered = useRef(false);

  const ITEMS_PER_PAGE = 12;

  // Load more function
  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMore || !enabled) {
      return;
    }

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      // If this is the first load, fetch both featured and all companions
      if (allCompanionsList.length === 0) {
        const [featuredData, allData] = await Promise.all([
          getFeaturedCompanions(),
          getAllCompanions(),
        ]);

        // Convert station companions to Companion interface
        const stationCompanionsData: Companion[] = stationCompanions.map(
          (sc) => ({
            mentorId: sc.companion_id || sc.id,
            firstName: sc.given_name?.split(' ')[0] || sc.alias || '',
            lastName: sc.given_name?.split(' ').slice(1).join(' ') || '',
            videoLoopUrl: sc.video_url || '',
            jobTitle: sc.title || '',
            location: '',
            industry: sc.capability || '',
            company: '',
            headline: sc.title || '',
            previewUrl: sc.image_url || '',
            videoIdleLoopUrl: sc.video_url || '',
            previewImageUrl: sc.image_url || sc.full_image_url || '',
            slug: sc.slug || '',
            status: 'active',
            call_to_action: sc.placeholder || '',
          }),
        );

        // Priority: Featured > All > Station (skip duplicates)
        // Create sets of IDs to track what we've already added
        const featuredIds = new Set(
          featuredData.map((c) => c.mentorId).filter(Boolean),
        );
        const featuredSlugs = new Set(
          featuredData.map((c) => c.slug).filter(Boolean),
        );

        // Filter out companions from allData that are already in featured
        const nonFeaturedData = allData.filter(
          (c) => !featuredIds.has(c.mentorId) && !featuredSlugs.has(c.slug),
        );

        // Track IDs from featured + all companions
        const allIds = new Set([
          ...featuredIds,
          ...nonFeaturedData.map((c) => c.mentorId).filter(Boolean),
        ]);
        const allSlugs = new Set([
          ...featuredSlugs,
          ...nonFeaturedData.map((c) => c.slug).filter(Boolean),
        ]);

        // Filter out station companions that are already in featured or all
        const uniqueStationCompanions = stationCompanionsData.filter(
          (c) => !allIds.has(c.mentorId) && !allSlugs.has(c.slug),
        );

        // Shuffle the non-featured companions and unique station companions
        const shuffledNonFeatured = shuffleArray(nonFeaturedData);
        const shuffledStationCompanions = shuffleArray(uniqueStationCompanions);

        // Combine: featured first, then shuffled non-featured, then shuffled station companions
        const combinedData = [
          ...featuredData,
          ...shuffledNonFeatured,
          ...shuffledStationCompanions,
        ];
        setAllCompanionsList(combinedData);

        // Load first chunk
        const firstChunk = combinedData.slice(0, ITEMS_PER_PAGE);
        setCompanions(firstChunk);
        offsetRef.current = ITEMS_PER_PAGE;
        setHasMore(combinedData.length > ITEMS_PER_PAGE);
      } else {
        // Load next chunk from already fetched data
        const nextChunk = allCompanionsList.slice(
          offsetRef.current,
          offsetRef.current + ITEMS_PER_PAGE,
        );
        setCompanions((prev) => [...prev, ...nextChunk]);
        offsetRef.current += ITEMS_PER_PAGE;
        setHasMore(offsetRef.current < allCompanionsList.length);
      }

      if (!initialLoadDone) {
        setInitialLoadDone(true);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load companions';
      setError(errorMessage);
      console.error('Error loading companions:', err);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [allCompanionsList, hasMore, enabled, initialLoadDone]);

  // Reset function
  const reset = useCallback(() => {
    setCompanions([]);
    setAllCompanionsList([]);
    offsetRef.current = 0;
    setHasMore(true);
    setError(null);
    setLoading(false);
    loadingRef.current = false;
    setInitialLoadDone(false);
    initialLoadTriggered.current = false;
  }, []);

  // Set up intersection observer for infinite scroll
  useEffect(() => {
    if (!enabled || !hasMore || !sentinelRef.current) return;

    // Disconnect any existing observer first
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Determine the root element for intersection observer
    const rootElement = scrollContainerRef?.current || null;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !loadingRef.current && hasMore) {
          loadMore();
        }
      },
      {
        root: rootElement,
        rootMargin,
        threshold: 0.1,
      },
    );

    observerRef.current.observe(sentinelRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [
    enabled,
    hasMore,
    initialLoadDone,
    loadMore,
    rootMargin,
    scrollContainerRef,
  ]);

  // Initial load effect
  useEffect(() => {
    if (!enabled || initialLoadTriggered.current) return;
    if (!initialLoadDone && companions.length === 0) {
      initialLoadTriggered.current = true;
      loadMore();
    }
  }, [enabled, initialLoadDone, companions.length, loadMore]);

  return {
    loading,
    hasMore,
    error,
    loadMore,
    reset,
    sentinelRef,
    companions,
  };
};

export default useAllCompanionsInfiniteScroll;
